import React, { useState, useEffect, useCallback, useRef, Fragment, FunctionComponent } from 'react'
import Button from '@material-ui/core/Button'
import TextField from '@material-ui/core/TextField'
import AttachFileIcon from '@material-ui/icons/AttachFile'
import HighlightOffIcon from '@material-ui/icons/HighlightOff'
import { Fab, IconButton, InputAdornment, LinearProgress, makeStyles, Theme } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import PlaylistAddIcon from '@material-ui/icons/PlaylistAdd'
import RemoveIcon from '@material-ui/icons/Remove'
import { ChatMessage } from '../../types/ChatContracts'
import { useDropzone } from 'react-dropzone'
import { Typography, useTheme } from '@material-ui/core'
import DownloadIcon from '@material-ui/icons/GetApp'
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile'
import PermMediaIcon from '@material-ui/icons/PermMedia'
import GraphicEqIcon from '@material-ui/icons/GraphicEq'
import ElectronIPC, { DownloadProgressInfo } from '../../services/ElectronIPC'
import * as FileTypes from '../../services/FileTypes'
import * as ChatDownloadUrl from '../../services/ChatDownloadUrl'
import prettyBytes from 'pretty-bytes'
import path from 'path'
import LocalChatFilesManifest from '../../services/LocalChatFilesManifest'
import useStyles from './ChatboxStyles'
import ChatTabButton from './ChatTabButton'
import clsx from 'clsx'
import useMuseStyles from '../../themes/useMuseStyles'
import { env } from '../../services/EnvironmentVariables'
import { api_base_path } from '../../services/Constants'
import { formatBytes, formatTimestamp } from '../../utils'
import useCentralizedChatManager, { FileUploadProgressData } from '../../components/useCentralizedChatManager'
import Linkify from 'linkify-react'
import * as linkify from 'linkifyjs'
import useAppNotificationActions from '../../functionalities/AppNotification/useAppNotificationActions'
import useRootSnackbar from '../../services/useRootSnackbar'
import { useTranslation } from 'react-i18next'
import { logger } from '../../services/Logger'
import CircularProgressWithLabel from '../../UIComponents/CircularProgressWithLabel'
import { AnalyticsEvents } from '../../services/AnalyticsService'
import useAnalytics from '../../services/useAnalytics'
import _ from 'lodash'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'

const log = logger.createChildLogger({ name: 'Chatbox' })

export type ChatboxTabTypes = 'messages' | 'files'

function LegacyChatbox({
    chatManager,
    isMessagesVisible = true,
    enableFiles = true,
    messageBoxHeight,
    EmptyMessagesComponent,

    onUnreadMessagesCtChanged = () => {},
    onSendMessage = null,
}: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    isMessagesVisible?: boolean
    enableFiles?: boolean
    messageBoxHeight?: string
    EmptyMessagesComponent?: JSX.Element

    onUnreadMessagesCtChanged?: (count: number) => void
    onSendMessage?: ((message: string) => void) | null
}) {
    const gclasses = useMuseStyles(useTheme())

    const { t } = useTranslation()

    const { isLoading, messageHistory, stats } = chatManager

    const [isDragMode, set_isDragMode] = useState<boolean>(false)
    const [hasMoreText, set_hasMoreText] = useState<boolean>(false)
    const [unreadMessagesCt, set_unreadMessagesCt] = useState<number>(0)
    const [currentTab, set_currentTab] = useState<ChatboxTabTypes>('messages')
    const [currentSelectedFiles, set_currentSelectedFiles] = useState<{ [key: string]: boolean }>({})
    const [forceRenderCounter, set_forceRenderCounter] = useState(0)

    const [scrollToBottomCtr, set_scrollToBottomCtr] = useState(0)

    const chatboxContainer = useRef(null)
    const dragzoneContainer = useRef(null)
    const scrollContainer = useRef<HTMLDivElement>(null)

    const downloadLockedFilesRef = useRef<Set<string>>(new Set())
    const messageHistoryLengthRef = useRef(0)

    const classes = useStyles(useTheme())

    const { logUserActivityWithContext } = useAnalytics()

    const { showAppNotification } = useAppNotificationActions()

    // message batching
    const [msgLoadedBatchState, _set_msgLoadedBatchState] = useState<{ headIdx: number; loadedBatchesCt: number }>({
        headIdx: 0,
        loadedBatchesCt: 1,
    })
    const msgLoadedBatchStateRef = useRef(msgLoadedBatchState)
    const BATCH_SIZE = 30

    const setMsgLoadedBatchState = (s: { headIdx: number; loadedBatchesCt: number }) => {
        if (!_.isEqual(s, msgLoadedBatchStateRef.current)) {
            log.verbose('setMsgLoadedBatchState ', '', s)
            msgLoadedBatchStateRef.current = s
            _set_msgLoadedBatchState(s)
        }
    }

    useEffect(() => {
        // Register scroll handler
        if (!scrollContainer.current) {
            return
        }

        scrollContainer.current.addEventListener('scroll', scrollHandler, {
            capture: true, // Get everything that scrolls inside the div
        })

        return () => {
            scrollContainer.current?.removeEventListener('scroll', scrollHandler)
        }

        // Reload when `isMessagesVisible` -- when the chatbox is opened/closed
    }, [isMessagesVisible])

    useEffect(() => {
        if (chatManager == null) return
        chatManager.loadMessages()
    }, [])

    const chatboxNotificationRefractoryTimerRef = useRef<NodeJS.Timeout | null>(null)

    useEffect(() => {
        chatManager.setOnFileDownloadCompleteHandler((fileUuid: string, filename: string) => {
            downloadLockedFilesRef.current.delete(fileUuid)
            log.verbose(
                'setOnFileDownloadCompleteHandler',
                `clearing fileUuid="${fileUuid}" from downloadLockedFilesRef as download has completed.`
            )

            if (!chatboxNotificationRefractoryTimerRef.current) {
                showAppNotification({
                    title: 'File downloaded',
                    body: `"${filename || 'A file'}" completed downloading.`,
                })
                chatboxNotificationRefractoryTimerRef.current = setTimeout(() => {
                    chatboxNotificationRefractoryTimerRef.current = null
                }, 5000)
            }
        })
    }, [])

    useEffect(() => {
        chatManager.setOnNewMessageHandler((chatmsg: ChatMessage) => {
            if (isMessagesVisible) {
                return
            }

            // Show notification
            if (!chatboxNotificationRefractoryTimerRef.current) {
                console.log(
                    `chatbox: at chatManager.setOnNewMessageHandler callback: calling ElectronIPC.showNotification().`
                )
                if (chatmsg.contentType === 'text') {
                    showAppNotification({ title: 'New Message', body: chatmsg.body })
                } else if (chatmsg.contentType === 'file') {
                    showAppNotification({ title: 'New File', body: `"${chatmsg.body.filename}" has been uploaded.` })
                }
                chatboxNotificationRefractoryTimerRef.current = setTimeout(() => {
                    chatboxNotificationRefractoryTimerRef.current = null
                }, 5000)
            }

            set_unreadMessagesCt((ct) => {
                return ct + 1
            })
        })
    }, [chatManager, isMessagesVisible, unreadMessagesCt])

    useEffect(() => {
        if (isMessagesVisible) {
            set_unreadMessagesCt(0)
        }
    }, [isMessagesVisible, hasMoreText])

    useEffect(() => {
        // scroll to bottom when the user 'opens the chat box" when there
        // are unread messages.
        if (isMessagesVisible && unreadMessagesCt > 0) {
            set_scrollToBottomCtr((c) => c + 1)
        }
    }, [isMessagesVisible, unreadMessagesCt])

    useEffect(() => {
        onUnreadMessagesCtChanged(unreadMessagesCt)
    }, [unreadMessagesCt])

    /**
     * While dragover event on `chatboxContainer` ref, set isDragMode state to 'true'.
     * 1 second after the dragover event stops firing, isDragMode is set to 'false'.
     */
    useEffect(() => {
        const node: any = chatboxContainer.current
        if (node == null) return

        let timer: any = null
        const handleDragover = (ev: any) => {
            if (timer != null) {
                clearTimeout(timer)
            }
            timer = setTimeout(() => {
                set_isDragMode(false)
            }, 1000)
            set_isDragMode(true)
        }

        node.addEventListener('dragover', handleDragover, false)
        return () => {
            node.removeEventListener('dragover', handleDragover)
        }
    }, [chatboxContainer.current])

    const scrollHandler = (e: any) => {
        const target: HTMLDivElement = e.target
        // console.log(`at scrollHandler: e.target.scrollHeight=${target.scrollHeight}, ${target.scrollTop}, ${target.clientHeight}`)

        const margin_of_error = 3
        const hasMoreAtBottom = target.scrollTop + margin_of_error + target.clientHeight < target.scrollHeight
        // console.log(`at scrollHandler: hasMoreAtBottom=${hasMoreAtBottom}`)
        set_hasMoreText(hasMoreAtBottom)

        if (target.scrollTop < 100) {
            // load next batch if there's more to load.
            if (msgLoadedBatchStateRef.current.loadedBatchesCt * BATCH_SIZE < messageHistoryLengthRef.current) {
                let loadedBatchesCt = msgLoadedBatchStateRef.current.loadedBatchesCt + 1
                let headIdx = Math.max(0, messageHistoryLengthRef.current - loadedBatchesCt * BATCH_SIZE)
                setMsgLoadedBatchState({ loadedBatchesCt, headIdx })
            }
        }

        if (!hasMoreAtBottom) {
            setMsgLoadedBatchState({
                loadedBatchesCt: 1,
                headIdx: Math.max(0, messageHistoryLengthRef.current - 1 * BATCH_SIZE),
            })
        }
    }

    const scrollToBottom = () => {
        if (isMessagesVisible) {
            set_unreadMessagesCt(0)
        }
        set_scrollToBottomCtr((n) => n + 1)

        setMsgLoadedBatchState({
            loadedBatchesCt: 1,
            headIdx: Math.max(0, messageHistoryLengthRef.current - 1 * BATCH_SIZE),
        })
    }

    useEffect(() => {
        if (!isLoading) {
            setTimeout(() => {
                scrollToBottom()
            }, 25)
        }
    }, [isLoading])

    const sendMessageHandler = (text: string) => {
        if (chatManager == null) {
            throw new Error(`Chatbox: chatManager is null.`)
        }

        if (text.trim().length > 0) {
            chatManager.sendText(text)
            onSendMessage && onSendMessage(text)
        }
    }

    const onCellSelectedHandler = (id: string, is: boolean) => {
        if (is) {
            set_currentSelectedFiles((set) => ({ ...set, [id]: true }))
        } else {
            set_currentSelectedFiles((set) => {
                const next = { ...set }
                delete next[id]
                return next
            })
        }
    }

    const onDeleteSelectedCellsHandler = () => {
        chatManager?.deleteMessages(Object.keys(currentSelectedFiles))
        set_currentSelectedFiles({})
    }

    const onAttachFileHandler = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!chatManager) {
            return
        }

        if (!e.target.files) {
            return
        }

        const files: File[] = Array.from(e.target.files)
        // NOTE: for now, only support uploading one file at a time. The open file dialog
        // from <input> only allows you to select one file anyway.
        if (files && files[0]) {
            chatManager.sendFile(files)
        }
    }

    useEffect(() => {
        if (scrollContainer.current) {
            scrollContainer.current.scrollTop = scrollContainer.current.scrollHeight
        }
    }, [scrollToBottomCtr])

    // if messageHistory + messagePendingLookup length changes, update scroll-to-bottom
    const prevListLength = useRef(0)
    useEffect(() => {
        messageHistoryLengthRef.current = messageHistory.length

        const pendingMessagesCt = chatManager.messagePendingLookup.size || 0
        const listLength = messageHistory.length + pendingMessagesCt

        // if not at bottom, don't auto scroll.
        if (hasMoreText) {
            // no-op
        } else {
            if (listLength > prevListLength.current) {
                scrollToBottom()
            }
        }

        prevListLength.current = listLength

        // update messageHistory headIdx when message history is updated.
        const s = msgLoadedBatchStateRef.current
        setMsgLoadedBatchState({
            ...s,
            headIdx: Math.max(0, messageHistory.length - s.loadedBatchesCt * BATCH_SIZE),
        })
    }, [messageHistory, chatManager.messagePendingLookup, hasMoreText])

    log.isSilly() && log.silly('RENDER', `messageHistoryHeadIdx=${JSON.stringify(msgLoadedBatchState)}`)

    if (chatManager == null) {
        return <div>Component loading...</div>
    }

    if (isLoading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Typography variant="body1">Loading...</Typography>
            </div>
        )
    }

    return (
        <>
            {enableFiles && (
                <div className={classes.buttonContainer}>
                    <ChatTabButton
                        selected={currentTab === 'messages'}
                        onClick={() => {
                            set_currentTab('messages')
                            set_currentSelectedFiles({})
                        }}
                    >
                        {t('Messages')}
                    </ChatTabButton>
                    <ChatTabButton selected={currentTab === 'files'} onClick={() => set_currentTab('files')}>
                        {t('Files')}
                    </ChatTabButton>
                </div>
            )}

            <div
                id="dropzone-container"
                ref={chatboxContainer}
                style={{ display: 'grid', gridTemplate: '1fr / 1fr', placeItems: 'center', height: '100%' }}
            >
                <div
                    id="chatbox-container"
                    className={classes.chatContentContainer}
                    style={messageBoxHeight ? { height: messageBoxHeight } : { height: '100%' }}
                >
                    <div
                        id="chatbox-messages-container"
                        style={{ position: 'relative', flexGrow: 2, overflowY: 'hidden' }}
                    >
                        {Object.keys(currentSelectedFiles).length > 0 && (
                            <div
                                id="fab-container"
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'absolute',
                                    right: 0,
                                    bottom: 0,
                                    margin: '1rem',
                                }}
                            >
                                <div title="Deselect all">
                                    <Fab
                                        color="primary"
                                        size="small"
                                        style={{ zIndex: 10000, backgroundColor: 'orange', marginBottom: '0.5rem' }}
                                        onClick={() => set_currentSelectedFiles({})}
                                    >
                                        <RemoveIcon />
                                    </Fab>
                                </div>
                                <div title="Select all files">
                                    <Fab
                                        color="primary"
                                        size="small"
                                        style={{ zIndex: 10000, backgroundColor: 'orange', marginBottom: '0.5rem' }}
                                        onClick={() => {
                                            const ids: { [key: string]: boolean } = {}
                                            messageHistory
                                                .filter((message) => message.contentType === 'file')
                                                .forEach((message) => (ids[message.id] = true))
                                            set_currentSelectedFiles(ids)
                                        }}
                                    >
                                        <PlaylistAddIcon />
                                    </Fab>
                                </div>
                                <div title="Delete (cannot be undone)">
                                    <Fab
                                        color="primary"
                                        size="small"
                                        style={{ zIndex: 10000, backgroundColor: 'red' }}
                                        onClick={onDeleteSelectedCellsHandler}
                                    >
                                        <DeleteIcon />
                                    </Fab>
                                </div>
                            </div>
                        )}

                        <div id="scroll-container" ref={scrollContainer} className={classes.scrollableSection}>
                            <MessageListContainer
                                chatManager={chatManager}
                                messageHistory={
                                    currentTab === 'files'
                                        ? messageHistory.filter((message) => message.contentType === 'file')
                                        : messageHistory.slice(msgLoadedBatchState.headIdx)
                                }
                                currentTab={currentTab}
                                currentSelectedFiles={currentSelectedFiles}
                                onCellSelectedHandler={onCellSelectedHandler}
                                downloadLockedFilesRef={downloadLockedFilesRef}
                                EmptyMessagesComponent={EmptyMessagesComponent}
                            />
                        </div>

                        <ScrollArrow direction="down" show={hasMoreText} onClick={() => scrollToBottom()} />
                    </div>

                    <div id="messages-textfield-container" style={{ boxShadow: '0px -2px 5px -1px #111111' }}>
                        {currentTab === 'messages' && (
                            <>
                                <TextField
                                    id="outlined-multiline-flexible"
                                    style={{ width: '100%' }}
                                    placeholder={t('Write a message...') || ''}
                                    className={classes.chatTextBox}
                                    multiline
                                    maxRows={4}
                                    defaultValue=""
                                    onKeyDown={(e: any) => {
                                        if (e.key === 'Enter') {
                                            e.preventDefault()
                                            if (e.shiftKey) {
                                                e.target.value += '\n'
                                                set_forceRenderCounter((c) => c + 1)
                                            } else {
                                                sendMessageHandler(e.target.value)
                                                e.target.value = ''
                                                logUserActivityWithContext(AnalyticsEvents.SENT_CHAT_MESSAGE)
                                            }
                                        }
                                    }}
                                    InputProps={
                                        enableFiles
                                            ? {
                                                  startAdornment: (
                                                      <InputAdornment position="start">
                                                          <Button
                                                              variant="contained"
                                                              component="label"
                                                              className={clsx(gclasses.buttonSmall)}
                                                              style={{
                                                                  padding: 0,
                                                                  minWidth: 0,
                                                                  backgroundColor: 'transparent',
                                                              }}
                                                              size="small"
                                                          >
                                                              <AttachFileIcon
                                                                  style={{
                                                                      transform: 'rotate(45deg)',
                                                                      width: '1.3rem',
                                                                      color: '#F9CC04',
                                                                  }}
                                                              />
                                                              <input
                                                                  type="file"
                                                                  onChange={(e) => onAttachFileHandler(e)}
                                                                  hidden
                                                              />
                                                          </Button>
                                                      </InputAdornment>
                                                  ),
                                              }
                                            : {}
                                    }
                                    variant="outlined"
                                />
                                <Typography
                                    variant="body1"
                                    style={{ textAlign: 'center', color: '#5D5D5D', fontSize: '90%' }}
                                >
                                    {t('Press Shift+Enter for new line')}
                                </Typography>
                                <div style={{ marginBottom: '0.5rem' }}></div>
                            </>
                        )}
                        {currentTab === 'files' && (
                            <div style={{ padding: '0.8rem 1.3rem 0.5rem 1.3rem' }}>
                                <MessageBoxFileUsageProgressbar
                                    value={((stats.fileSpaceUsage || 0) / (stats.fileStorageSize || 1)) * 100}
                                    postLabel={`${formatBytes(stats.fileSpaceUsage)} / ${formatBytes(stats.fileStorageSize)}`}
                                />
                                <Typography
                                    variant="body1"
                                    style={{
                                        textAlign: 'center',
                                        color: '#5D5D5D',
                                        fontSize: '90%',
                                        marginTop: '0.5rem',
                                    }}
                                >
                                    {t('Click on the file icon to select')}
                                </Typography>
                            </div>
                        )}
                    </div>
                </div>

                {enableFiles && (
                    <div
                        id="dragzone"
                        ref={dragzoneContainer}
                        style={{
                            gridColumn: '1 / 1',
                            gridRow: '1 / 1',
                            zIndex: isDragMode ? 10 : 0,
                            width: '100%',
                            height: '100%',
                        }}
                    >
                        <MyDropzone chatManager={chatManager} />
                    </div>
                )}
            </div>
        </>
    )
}

function MessageBoxFileUsageProgressbar({ value, postLabel }: { value: number | null | undefined; postLabel: string }) {
    const { t } = useTranslation()

    if (value && isNaN(value)) {
        // We regard NaN as a null.
        value = null
    }

    return (
        <>
            <Typography variant="body1" style={{ color: '#aaa', fontSize: '90%' }}>
                {t('Storage usage:')}
            </Typography>
            <div style={{ display: 'flex', flexDirection: 'column', width: '100%', margin: '0.25rem 0' }}>
                <LinearProgress variant="determinate" value={value || 0} style={{ flexGrow: 2, height: '10px' }} />
                <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginTop: '3px', textAlign: 'end', fontSize: '80%' }}
                >
                    {postLabel}
                </Typography>
            </div>
        </>
    )
}

function MessageListContainer({
    chatManager,
    messageHistory,
    currentTab,
    currentSelectedFiles,
    onCellSelectedHandler,
    downloadLockedFilesRef,
    EmptyMessagesComponent,
}: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    messageHistory: ChatMessage[]
    currentTab: ChatboxTabTypes
    currentSelectedFiles: { [key: string]: boolean }
    onCellSelectedHandler: (id: string, is: boolean) => void
    downloadLockedFilesRef: React.MutableRefObject<Set<string>>
    EmptyMessagesComponent?: JSX.Element
}) {
    const { t } = useTranslation()

    const pendingMessagesCt = chatManager.messagePendingLookupRef.current?.size || 0

    if (messageHistory.length === 0 && pendingMessagesCt === 0) {
        if (EmptyMessagesComponent) {
            return EmptyMessagesComponent
        } else {
            return (
                <div style={{ display: 'flex', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
                    <Typography variant="body1">{t('No messages')}</Typography>
                </div>
            )
        }
    }

    return (
        <MessageList
            chatManager={chatManager}
            messageHistory={messageHistory}
            currentTab={currentTab}
            currentSelectedFiles={currentSelectedFiles}
            downloadLockedFilesRef={downloadLockedFilesRef}
            onCellSelectedHandler={onCellSelectedHandler}
        />
    )
}

function MessageList({
    chatManager,
    messageHistory,
    currentTab,
    currentSelectedFiles,
    downloadLockedFilesRef,
    onCellSelectedHandler,
}: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    messageHistory: ChatMessage[]
    currentTab: ChatboxTabTypes
    currentSelectedFiles: { [key: string]: boolean }
    downloadLockedFilesRef: React.MutableRefObject<Set<string>>
    onCellSelectedHandler: (id: string, is: boolean) => void
}) {
    const { messagePendingLookupRef } = chatManager

    const messagePendingArray = Array.from(messagePendingLookupRef.current)
    const totalItems = [...messageHistory, ...messagePendingArray]

    const renderer = useCallback(
        (
            index: number,
            messageHistory: ChatMessage[],
            chatManager: ReturnType<typeof useCentralizedChatManager>,
            currentTab: ChatboxTabTypes,
            currentSelectedFiles: { [key: string]: boolean },
            downloadLockedFilesRef: React.MutableRefObject<Set<string>>,
            messagePendingArray: [string, ChatMessage][],
            onCellSelectedHandler: (id: string, is: boolean) => void
        ) => {
            // console.log(`*** MessageList renderer: index=${index} messageHistory=%O fileUploadProgressLookup=%O`,
            //     messageHistory, fileUploadProgressLookup)
            const { fileDownloadProgressLookup, fileUploadProgressLookup } = chatManager

            const chatmsg = messageHistory[index]

            if (index < messageHistory.length) {
                return (
                    <Fragment key={chatmsg.id + '-container'}>
                        {chatmsg.contentType === 'text' && (
                            <MessageCellText key={chatmsg.id} chatManager={chatManager} chatmsg={chatmsg} />
                        )}
                        {chatmsg.contentType === 'file' && (
                            <MessageCellFile
                                key={chatmsg.id}
                                chatManager={chatManager}
                                chatmsg={chatmsg}
                                currentTab={currentTab}
                                fileDownloadProgressLookup={fileDownloadProgressLookup}
                                isSelected={currentSelectedFiles[chatmsg.id] === true}
                                downloadLockedFilesRef={downloadLockedFilesRef}
                                onCellSelected={(is) => onCellSelectedHandler(chatmsg.id, is)}
                            />
                        )}
                    </Fragment>
                )
            } else {
                index = index - messageHistory.length

                if (messagePendingArray[index]) {
                    const [key, chatmsg] = messagePendingArray[index]
                    const fileProgressData = fileUploadProgressLookup.get(key)
                    return (
                        <MessageCellProgress
                            key={key}
                            chatManager={chatManager}
                            id={key}
                            chatmsg={chatmsg}
                            fileProgressData={fileProgressData}
                        />
                    )
                } else {
                    return <div style={{ height: 1 }}></div>
                }
            }
        },
        []
    )

    // NOTE:
    // Moved away from using Virtuoso because it sometimes did not fire the `renderer()`
    // (see the `itemContent` callback) when the window was in a modal. See #774.
    //
    // NOTE:
    // "List does not scroll to the bottom / items jump around#
    // "This is the most common setup error. It happens because the DOM elements inside the items
    // (or the items themselves) have margins. Margins are difficult to measure, not to mention the
    // collapsing behavior. Some elements (for example, p, h*, ul) have default margins."
    // https://virtuoso.dev/troubleshooting/
    // return <Virtuoso
    //     ref={virtuoso}
    //     style={{height:'100%'}}
    //     totalCount={totalItemsCount}
    //     itemContent={index => renderer(index)}
    //     followOutput={true}
    // />

    return (
        <>
            {totalItems.map((_, i) => {
                return renderer(
                    i,
                    messageHistory,
                    chatManager,
                    currentTab,
                    currentSelectedFiles,
                    downloadLockedFilesRef,
                    messagePendingArray,
                    onCellSelectedHandler
                )
            })}
        </>
    )
}

function MessageCellHeader({ userDisplayName, timestamp }: { userDisplayName: string; timestamp: Date }) {
    const classes = useStyles()

    return (
        <div style={{ display: 'flex', alignItems: 'baseline', paddingBottom: '0.2rem' }}>
            <Typography variant="body2" className={classes.chatSender}>
                {userDisplayName}
            </Typography>
            <Typography variant="body1" className={classes.chatTimestamp}>
                {formatTimestamp(timestamp)}
            </Typography>
        </div>
    )
}

function MessageCellText({
    chatManager,
    chatmsg,
}: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    chatmsg: ChatMessage
}) {
    const classes = useStyles()

    // resolve chatmsg.senderUrn to a username/name.
    const userDisplayName = chatManager.resolveMessageUsername(chatmsg.senderUrn) || chatmsg.senderUrn

    // @ts-ignore
    const str: string = chatmsg.body
    const hasLinks = linkify.find(str)

    if (!hasLinks) {
        return (
            <div className={classes.chatCellContainer}>
                <MessageCellHeader userDisplayName={userDisplayName} timestamp={chatmsg.timestamp} />
                {
                    <Typography variant="body1" className={classes.chatMessage}>
                        {str}
                    </Typography>
                }
            </div>
        )
    } else {
        const renderLink = ({ attributes, content }: { attributes: any; content: any }) => {
            const { href, ...props } = attributes
            return (
                <a
                    className="chat-hyperlink"
                    href={'#'}
                    onClick={(e) => {
                        e.preventDefault()
                        ElectronIPC.openLinkInBrowser(href)
                    }}
                >
                    {content}
                </a>
            )
        }

        return (
            <div className={classes.chatCellContainer}>
                <MessageCellHeader userDisplayName={userDisplayName} timestamp={chatmsg.timestamp} />
                <Typography variant="body1" className={classes.chatMessage}>
                    <Linkify options={{ render: renderLink }}>{str}</Linkify>
                </Typography>
            </div>
        )
    }
}

function FileIcon(props: { fileName: string }) {
    const fileType = path.extname(props.fileName).toLowerCase().substring(1)

    if (
        [
            'mp4',
            'mov',
            'wmv',
            'flv',
            'avi',
            'avchd',
            'webm',
            'mkv',
            'jpg',
            'jpeg',
            'png',
            'gif',
            'tiff',
            'svg',
        ].includes(fileType)
    ) {
        return <PermMediaIcon />
    } else if (FileTypes.isAudioFile(props.fileName)) {
        return <GraphicEqIcon />
    } else {
        return <InsertDriveFileIcon />
    }
}

function MessageCellFile(props: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    chatmsg: ChatMessage
    currentTab: ChatboxTabTypes
    fileDownloadProgressLookup: Map<string, DownloadProgressInfo>
    isSelected: boolean
    downloadLockedFilesRef: React.MutableRefObject<Set<string>>
    onCellSelected: (isSelected: boolean) => void
}) {
    const classes = useStyles()
    const { chatManager, chatmsg, fileDownloadProgressLookup, isSelected, downloadLockedFilesRef, onCellSelected } =
        props

    const [refreshKey, set_refreshKey] = useState<number>(0)
    const [showDeleteFileButton, set_showDeleteFileButton] = useState<boolean>(false)
    const showDeleteFileButtonTimerRef = useRef<NodeJS.Timeout | null>(null)

    const [isConfirmDeletion, set_isConfirmDeletion] = useState<boolean>(false)

    if (chatmsg.contentType !== 'file') {
        throw new Error(`Contract error: MessageCellFile used when chatmsg.contentType !== "file"!`)
    }

    const onDownload = (
        ev: any,
        filename: string,
        fileSize: number,
        fileUuid: string,
        actionAfter: 'none' | 'open' | 'show-in-folder'
    ) => {
        ev.preventDefault()

        // For audio files, always show in folder.
        if (FileTypes.isAudioFile(filename) && actionAfter === 'open') {
            actionAfter = 'show-in-folder'
        }

        // downloadLockedFilesRef is for preventing fast multi-clicking download by accident.
        if (fileDownloadProgressLookup.has(fileUuid)) {
            log.verbose('onDownload', `fileDownloadProgressLookup has fileUuid="${fileUuid}"; no-op.`)
        } else {
            if (downloadLockedFilesRef.current.has(fileUuid)) {
                log.verbose(
                    'onDownload',
                    `downloadLockedFilesRef has fileUuid="${fileUuid}". downloadLockedFilesRef ` +
                        `is for preventing fast multi-clicking download by accident. no-op.`
                )
            } else {
                log.verbose('onDownload', `add fileUuid="${fileUuid}" to downloadLockedFilesRef.`)
                downloadLockedFilesRef.current.add(fileUuid)

                log.verbose('onDownload', `call chatManager.triggerDownloadFile with parameters:`, {
                    filename,
                    fileSize,
                    fileUuid,
                    actionAfter,
                })
                chatManager.triggerDownloadFile(filename, fileSize, fileUuid, actionAfter)
                setTimeout(() => {
                    log.verbose('onDownload', `clear fileUuid="${fileUuid}" from downloadLockedFilesRef.`)
                    downloadLockedFilesRef.current.delete(fileUuid)
                }, 3000)
            }
        }
    }

    // resolve chatmsg.senderUrn to a username/name.
    const userDisplayName = chatManager.resolveMessageUsername(chatmsg.senderUrn) || chatmsg.senderUrn

    // Is this file currently being downloaded?
    const dlInfo: DownloadProgressInfo | undefined = fileDownloadProgressLookup.get(chatmsg.body.link)
    const isBeingDownloaded = dlInfo != null
    const isDownloadError = dlInfo?.state === 'interrupted' || dlInfo?.state === 'cancelled'
    const isInCache = LocalChatFilesManifest.exists(chatmsg.body.link)
    const isDraggable = chatmsg.contentType === 'file' && !isBeingDownloaded

    const fileProgressPct = dlInfo
        ? Math.round(((dlInfo?.transferredBytes || 0) / (chatmsg.body.size || Number.MAX_SAFE_INTEGER)) * 100)
        : 0
    const fpLoadedText = prettyBytes(dlInfo?.transferredBytes || 0)
    const fpTotalText = prettyBytes(chatmsg.body.size)

    const onMouseEnter = async () => {
        if (!isDraggable) {
            return
        }

        const cacheData = LocalChatFilesManifest.get(chatmsg.body.link)
        if (cacheData) {
            if ((await FileTypes.verifyFile(cacheData.savePath)) === false) {
                LocalChatFilesManifest.invalidate(chatmsg.body.link)
                set_refreshKey((key) => key + 1)
            }
        }

        showDeleteFileButtonTimerRef.current = setTimeout(() => {
            set_showDeleteFileButton(true)
        }, 250)
    }

    const onMouseLeave = async () => {
        if (showDeleteFileButtonTimerRef.current) {
            clearTimeout(showDeleteFileButtonTimerRef.current)
        }
        set_showDeleteFileButton(false)
    }

    const onDragStart = async (ev: React.DragEvent<HTMLDivElement>) => {
        if (!isDraggable) {
            ev.preventDefault()
            return
        }

        const cacheData = LocalChatFilesManifest.get(chatmsg.body.link)
        if (!cacheData) {
            // In case a local copy doesn't exist, we do the next best thing which is to use DownloadURL.
            const url = ChatDownloadUrl.fromFileUuidToUrl(api_base_path, chatManager.roomUrn, 'room', chatmsg.body.link)
            const info = `${chatmsg.body.mimeType}:${chatmsg.body.filename}:${url}`
            ev.dataTransfer.setData('DownloadURL', info)
            return
        }

        ev.preventDefault()
        console.log(`Chatbox: dragfile: Resolved savePath="${cacheData.savePath}".`)
        // We trust the cache because it's already been validated at onMouseEnter.

        // https://www.electronjs.org/docs/tutorial/native-file-drag-drop
        ElectronIPC.fileDragStart({ filePath: cacheData.savePath })
    }

    const onFilenameClick = (ev: any) => {
        onDownload(ev, chatmsg.body.filename, chatmsg.body.size, chatmsg.body.link, 'open')
    }

    return (
        <div key={refreshKey} className={classes.chatCellContainer}>
            <MessageCellHeader userDisplayName={userDisplayName} timestamp={chatmsg.timestamp} />
            <div
                className={classes.fileContainer}
                style={isSelected ? { backgroundColor: '#f9cc04', position: 'relative' } : { position: 'relative' }}
                onMouseEnter={isDraggable ? onMouseEnter : undefined}
                onMouseLeave={isDraggable ? onMouseLeave : undefined}
                onDragStart={onDragStart}
                draggable={isDraggable}
            >
                {!isConfirmDeletion && (
                    <div style={{ display: 'inline-block', width: '20%' }}>
                        {isBeingDownloaded ? (
                            <CircularProgressWithLabel value={fileProgressPct} />
                        ) : (
                            <div
                                className={classes.rectangle}
                                onClick={(ev) => {
                                    if (props.currentTab === 'messages') {
                                        onFilenameClick(ev)
                                    } else if (props.currentTab === 'files') {
                                        onCellSelected(!isSelected)
                                    }
                                }}
                            >
                                {isInCache ? <FileIcon fileName={chatmsg.body.filename} /> : <DownloadIcon />}
                            </div>
                        )}
                    </div>
                )}
                <div style={{ display: 'inline-block', width: isConfirmDeletion ? '100%' : '80%', paddingLeft: '5px' }}>
                    {isBeingDownloaded ? (
                        <>
                            <a className={classes.fileLink}>{chatmsg.body.filename}</a>
                            {isDownloadError ? (
                                <>
                                    <Typography className={classes.fileboxSubtext} variant="body2">
                                        Failed to download the file.
                                    </Typography>
                                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                        <a
                                            href="#"
                                            className={classes.fileSublink}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                chatManager.cancelFileDownload(chatmsg.body.link)
                                            }}
                                        >
                                            Cancel
                                        </a>
                                        {/* TODO <a href='#' className={classes.fileSublink} onClick={onRetry}>Retry</a> */}
                                    </div>
                                </>
                            ) : (
                                <Typography className={classes.fileboxSubtext} variant="body2">
                                    ({fpLoadedText} / {fpTotalText})
                                </Typography>
                            )}
                        </>
                    ) : (
                        <>
                            {isConfirmDeletion ? (
                                <>
                                    <Typography
                                        style={{
                                            color: '#f1f1f1',
                                            textAlign: 'center',
                                            padding: '4px 0',
                                            fontSize: '95%',
                                        }}
                                        variant="body2"
                                    >
                                        Are you sure you want to delete this file for everyone?
                                    </Typography>
                                    {/*<Typography className={classes.fileboxSubtext} style={{textAlign:'center'}} variant='body2'>
                                    Files are deleted for everyone.
                                </Typography>*/}
                                    <div
                                        className={classes.buttonContainer}
                                        style={{ paddingTop: 8, paddingBottom: 6 }}
                                    >
                                        <ChatTabButton
                                            style={{ height: 24 }}
                                            selected={false}
                                            onClick={() => {
                                                set_isConfirmDeletion(false)
                                            }}
                                        >
                                            Cancel
                                        </ChatTabButton>
                                        <ChatTabButton
                                            style={{ height: 24 }}
                                            selected={true}
                                            onClick={() => {
                                                chatManager?.deleteMessages([chatmsg.id])
                                            }}
                                        >
                                            Delete
                                        </ChatTabButton>
                                    </div>
                                </>
                            ) : (
                                <>
                                    <a
                                        href="#"
                                        className={classes.fileLink}
                                        style={isSelected ? { color: 'black' } : {}}
                                        onClick={(ev) => {
                                            ev.preventDefault()
                                            onFilenameClick(ev)
                                        }}
                                    >
                                        {chatmsg.body.filename}
                                    </a>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                        }}
                                    >
                                        <Typography className={classes.fileboxSubtext} variant="body2">
                                            {fpTotalText}
                                        </Typography>
                                        {isInCache ? (
                                            <a
                                                href="#"
                                                className={classes.fileSublink}
                                                onClick={(ev) => {
                                                    ev.preventDefault()
                                                    onDownload(
                                                        ev,
                                                        chatmsg.body.filename,
                                                        chatmsg.body.size,
                                                        chatmsg.body.link,
                                                        'show-in-folder'
                                                    )
                                                }}
                                            >
                                                {env.IS_ELECTRON ? (
                                                    <>Show in {env.OS === 'win32' ? 'Explorer' : 'Finder'}</>
                                                ) : (
                                                    <>Download</>
                                                )}
                                            </a>
                                        ) : (
                                            <a
                                                href="#"
                                                className={classes.fileSublink}
                                                onClick={(ev) => {
                                                    ev.preventDefault()
                                                    onDownload(
                                                        ev,
                                                        chatmsg.body.filename,
                                                        chatmsg.body.size,
                                                        chatmsg.body.link,
                                                        'none'
                                                    )
                                                }}
                                            >
                                                Download
                                            </a>
                                        )}
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </div>
                <div
                    style={{
                        display: 'flex',
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        color: '#C33E36',
                        backgroundColor: '#202122',
                        cursor: 'pointer',
                        transition: 'opacity 200ms, visibility 200ms',
                        ...(showDeleteFileButton
                            ? { opacity: 1, visibility: 'visible' }
                            : { opacity: 0, visibility: 'hidden' }),
                    }}
                    onClick={() => set_isConfirmDeletion(true)}
                >
                    <HighlightOffIcon style={{ width: '1.333rem', height: '1.333rem' }} />
                </div>
            </div>
        </div>
    )
}

function MessageCellProgress({
    chatManager,
    id,
    chatmsg,
    fileProgressData,
}: {
    chatManager: ReturnType<typeof useCentralizedChatManager>
    id: string
    chatmsg: ChatMessage
    fileProgressData?: FileUploadProgressData
}) {
    const classes = useStyles()

    // console.log(`*** rendering MessageCellProgress with chatmsg=%O fileProgressData=%O`, chatmsg, fileProgressData)

    if (chatmsg.contentType === 'text') {
        return (
            <div className={classes.chatCellContainer}>
                <Typography variant="body2" className={classes.chatSender}>
                    You:
                </Typography>
                <Typography style={{ opacity: '0.5' }} variant="body1" className={classes.chatMessage}>
                    {chatmsg.body}
                </Typography>
                <Typography
                    style={{ marginLeft: '1ch', fontSize: '75%', opacity: '0.5' }}
                    variant="body1"
                    className={classes.chatMessage}
                >
                    (Sending...)
                </Typography>
            </div>
        )
    } else if (chatmsg.contentType === 'file') {
        const fileProgressPct = fileProgressData
            ? Math.round(((fileProgressData.loaded || 0) / (fileProgressData.total || Number.MAX_SAFE_INTEGER)) * 100)
            : 0
        const fpLoadedText = prettyBytes(fileProgressData?.loaded || 0)
        const fpTotalText = prettyBytes(fileProgressData?.total || 0)

        if (fileProgressData?.error) {
            const code = fileProgressData.error.code
            let errorMessage = `There was an error while uploading the file. code=${code}`
            if (code === 'exceeded_max_file_size') {
                // @ts-ignore
                const { got, max } = fileProgressData.error
                errorMessage = `The file (${prettyBytes(got)}) is too big. Maximum allowed file size is ${prettyBytes(max)}.`
            } else if (code === 'exceeded_total_upload_size') {
                // @ts-ignore
                const { got, max } = fileProgressData.error
                errorMessage = `We ran out of space! You are allowed ${prettyBytes(max)} per room.`
            } else if (code === 'network') {
                errorMessage = `The file could not be uploaded due to network error.`
            }

            // Upload file error
            return (
                <div className={classes.chatCellContainer}>
                    <Typography variant="body2" className={classes.greyText}>
                        Failed to upload file
                    </Typography>
                    <div className={classes.fileContainer}>
                        <div className="flex">
                            <div style={{ display: 'inline-block', width: '20%' }}>
                                <div className={classes.rectangle}></div>
                            </div>
                            <div style={{ display: 'inline-block', width: '80%', paddingLeft: '5px' }}>
                                <p
                                    className={classes.fileLink}
                                    style={{
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        margin: 0,
                                    }}
                                >
                                    {chatmsg.body.filename}
                                </p>
                                <Typography className={classes.fileboxSubtext} variant="body2">
                                    {errorMessage}
                                </Typography>
                                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <a
                                        href="#"
                                        className={classes.fileSublink}
                                        onClick={(e) => {
                                            e.preventDefault()
                                            chatManager.cancelFileUpload(id)
                                        }}
                                    >
                                        Cancel
                                    </a>
                                    {/* TODO <a href='#' className={classes.fileSublink} onClick={onRetry}>Retry</a> */}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        } else {
            // Uploading file
            return (
                <div className={classes.chatCellContainer}>
                    <Typography variant="body2" className={classes.greyText}>
                        Uploading file...
                    </Typography>
                    <div className={classes.fileContainer}>
                        <div className="flex">
                            <div style={{ width: '20%' }}>
                                <CircularProgressWithLabel value={fileProgressPct} />
                            </div>
                            <div style={{ width: '80%', paddingLeft: '5px' }}>
                                <a className={classes.fileLink}>{chatmsg.body.filename}</a>
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                    <Typography className={classes.fileboxSubtext} variant="body2">
                                        ({fpLoadedText} / {fpTotalText})
                                    </Typography>
                                    <div style={{ alignSelf: 'stretch' }}>
                                        <a
                                            href="#"
                                            className={classes.fileSublink}
                                            onClick={(e) => {
                                                e.preventDefault()
                                                chatManager.cancelFileUpload(id)
                                            }}
                                        >
                                            Cancel
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )
        }
    } else {
        return null
    }
}

function MyDropzone({ chatManager }: { chatManager: ReturnType<typeof useCentralizedChatManager> }) {
    const { show: showSnackbar } = useRootSnackbar()

    const [isDragActiveFinal, set_isDragActiveFinal] = useState(false)

    const onDrop = useCallback((file: any) => {
        console.log(`Dropzone: file=%O`, file)
        // { name, path, size, type }
        // where type is the mime type, e.g. 'application/json'.
        if (Array.isArray(file) && file.length > 5) {
            // not supported
            showSnackbar({
                severity: 'error',
                message: 'You can only drag and drop up to 5 files at once',
            })
        } else {
            chatManager.sendFile(file)
        }
    }, [])
    const { getRootProps, isDragActive } = useDropzone({ onDrop })

    const zoneDivRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        console.log(`MyDropzone: isDragActive=${isDragActive}`)

        // let timer: NodeJS.Timer|null = null
        // if (isDragActive) {
        //     setInterval(() => {
        //         if (zoneDivRef.current) {
        //             // @ts-ignore
        //             const hoveringTestPassed = zoneDivRef.current?.parentNode.matches(":hover")
        //             // @ts-ignore
        //             console.log(`MyDropzone:\n zoneDivRef.current=${zoneDivRef.current}\n zoneDivRef.current?.matches(':hover')=${zoneDivRef.current?.matches(':hover')}\n zoneDivRef.current.parentNode=${zoneDivRef.current?.parentNode}\n zoneDivRef.current?.querySelector(':hover')=${zoneDivRef.current?.querySelector(':hover')}\n hoveringTestPassed=${hoveringTestPassed}`)
        //             if (!hoveringTestPassed) {
        //                 console.log(`MyDropzone: hovering test failed; setting isDragActiveFinal to false.`)
        //                 set_isDragActiveFinal(false)
        //             }
        //         }
        //     }, 500)
        // } else {
        //     timer && clearInterval(timer)
        // }

        set_isDragActiveFinal(isDragActive)
    }, [isDragActive])

    return (
        <div
            id="zone-div-ref-container"
            style={{ width: '100%', height: '100%' }}
            {...getRootProps()}
            onMouseLeave={() => {
                console.log(`MyDropzone: onMouseLeave()`)
                set_isDragActiveFinal(false)
            }}
        >
            {/* for click events: <input {...getInputProps()} />*/}
            {isDragActiveFinal && (
                <div
                    id="zone-div-ref"
                    ref={zoneDivRef}
                    style={{
                        display: 'flex',
                        width: '100%',
                        height: '100%',
                        backgroundColor: 'lightgray',
                        opacity: '0.5',
                    }}
                >
                    <p style={{ margin: 'auto' }}>Drop file here</p>
                </div>
            )}
            {!isDragActiveFinal && <div style={{ width: '100%', height: '100%' }}></div>}
        </div>
    )
}

const useScrollArrowStyles = makeStyles((theme: Theme) => ({
    scrollArrow: {
        width: '100%',
        height: '25px',
        zIndex: 1000,
        cursor: 'pointer',
        transition: 'opacity 0.4s',
        opacity: '1',
        position: 'absolute',
        pointerEvents: 'none',
        color: '#F1F1F1',
    },

    scrollDown: {
        bottom: '0px',
        background: 'linear-gradient(180deg, rgba(32,33,34,0) 0%, rgba(32,33,34,0.49) 60%, #000 100%)',
    },

    scrollUp: {
        top: '0px',
        background: 'linear-gradient(360deg, rgba(32,33,34,0) 0%, rgba(32,33,34,0.49) 60%, #000 100%)',
    },

    showArrow: { opacity: 1, pointerEvents: 'none' },

    hideArrow: { opacity: 0, pointerEvents: 'none' },
}))

const ScrollArrow: FunctionComponent<{ direction: 'up' | 'down'; onClick?: any; show?: boolean }> = (_props) => {
    const classes = useScrollArrowStyles(useTheme())
    const gclasses = useMuseStyles(useTheme())

    const props = {
        ..._props,
    }

    // If props.show not provided, default to 'true'.
    if (props.show == null) {
        props.show = true
    }

    // fix "Received `false` for a non-boolean attribute `show`."
    props.show = props.show ? true : undefined

    if (props.direction === 'up') {
        return (
            <div
                className={clsx(
                    classes.scrollArrow,
                    classes.scrollUp,
                    props.show ? classes.showArrow : classes.hideArrow
                )}
                style={{ display: 'flex', justifyContent: 'center' }}
            >
                <IconButton
                    className={gclasses.buttonNoLoadFlash}
                    style={{ pointerEvents: 'all', color: '#f1f1f1' }}
                    size="small"
                    onClick={props.onClick}
                >
                    <KeyboardArrowUpIcon direction={props.direction} />
                </IconButton>
            </div>
        )
    } else {
        return (
            <div
                className={clsx(
                    classes.scrollArrow,
                    classes.scrollDown,
                    props.show ? classes.showArrow : classes.hideArrow
                )}
                style={{ display: 'flex', justifyContent: 'center' }}
            >
                <IconButton
                    className={gclasses.buttonNoLoadFlash}
                    style={{ pointerEvents: 'all', color: '#f1f1f1' }}
                    size="small"
                    onClick={props.onClick}
                >
                    <KeyboardArrowDownIcon direction={props.direction} />
                </IconButton>
            </div>
        )
    }
}

export default LegacyChatbox
